import { computed } from 'vue'
import { EthereumNftTransactionExecutor } from '../transaction/transactionExecutor/EthereumNftTransactionExecutor'
import { SolanaNftTransactionExecutor } from '../transaction/transactionExecutor/SolanaNftTransactionExecutor'
import { useWalletStore, useCoinStore, useNFTContractStore } from '@/stores'
import type {
  Maybe,
  Account,
  Coin,
  NftContract,
  EncUVD,
  Sendable,
  ITransactionExecutor,
  ISendExtraData,
} from '@/types'
import type { INFT, NFTTokenContentType } from '@/types/api/nft'
import { replaceIPFSUrl } from '@/utils'

class Nft implements Sendable {
  id: string
  nftContractId: number
  accountId: string
  owner: string
  tokenId: string
  createdTime: string
  contentType: NFTTokenContentType
  quantity?: number | undefined
  externalLink: string
  imageUrl: string
  contentUrl?: string
  description: string
  state: number
  status?: string // PENDING | MINTING | CONFIRMED
  resizeImage?: { default: string; _256: string; _1024: string } | undefined
  name: string
  account: Maybe<Account>
  coin: Maybe<Coin>
  contract: Maybe<NftContract>
  percent?: string
  additionalUrl?: {
    image?: string
    content?: { name: string; description: string }
  }
  private _transactionExecutor: Maybe<ITransactionExecutor>
  // V2 필드 추가
  currencyId?: string
  contractAddress?: string
  collectionName?: string
  userAddress?: string
  network?: string
  standard?: string // ERC721, ERC1155

  constructor(nft: INFT) {
    this.id = nft.id
    this.name = nft.name || ''
    this.description = nft.description || ''
    this.imageUrl = nft.imageUrl || ''

    // V2 필드 처리 (V2는 nftContractId가 없고 contractAddress, currencyId 사용)
    const nftData = nft as any
    this.nftContractId = nft.nftContractId || 0 // V2에서는 0
    this.currencyId = nftData.currencyId
    this.contractAddress = nftData.contractAddress
    this.collectionName = nftData.nftContract?.name || nftData.collectionName
    this.userAddress = nftData.userAddress
    this.network = nftData.network || nftData.currencyId
    this.standard = nftData.standard // ERC721, ERC1155

    this.accountId = nftData.accountId || nft.accountId || ''
    this.owner = nft.owner || nftData.userAddress || ''
    this.tokenId = nft.tokenId || ''
    this.createdTime = nft.createdTime || (nft as any).createdAt || ''
    this.contentType = nft.contentType || 'IMAGE'
    this.quantity = nft.quantity
    this.externalLink = nft.externalLink || ''
    this.imageUrl = replaceIPFSUrl(nft.imageUrl || '')
    this.contentUrl = nft.contentUrl
    this.resizeImage = nft.resizeImage
    this.state = nft.state || 0
    this.status = nft.status // PENDING | MINTING | CONFIRMED
    this.additionalUrl = nft.addtionalUrl

    if (this.additionalUrl) {
      const { image, content } = this.additionalUrl
      const { name, description } = content as {
        name: string
        description: string
      }
      this.name = name
      this.description = description
      if (image) {
        this.imageUrl = image
      }
    }

    const computedAccount = computed(() => {
      const walletStore = useWalletStore()
      return walletStore.getAccount({ accountId: this.accountId })
    })
    const computedCoin = computed(() => {
      // V2: account의 coin 정보 사용 (currencyId 기반)
      if (this.currencyId && computedAccount.value) {
        return computedAccount.value.coin
      }
      // V1: contract의 coinId로 조회
      const coinStore = useCoinStore()
      return coinStore.coinList?.find(
        (coin) => coin.id === this.contract?.coinId,
      )
    })
    const computedContract = computed(() => {
      // V2: nftContractId가 0이면 contract 없음
      if (this.nftContractId === 0) {
        return null
      }
      // V1: nftContractId로 조회
      const nftContractStore = useNFTContractStore()
      return nftContractStore.getNFTContract(this.nftContractId)
    })

    Object.defineProperty(this, 'account', {
      get() {
        return computedAccount.value
      },
    })
    Object.defineProperty(this, 'coin', {
      get() {
        return computedCoin.value
      },
    })
    Object.defineProperty(this, 'contract', {
      get() {
        return computedContract.value
      },
    })
    Object.prototype.hasOwnProperty
  }

  public getKey(): string {
    return this.contract + (this.account?.id ?? '')
  }

  get transactionExecutor(): Maybe<ITransactionExecutor> {
    if (!this._transactionExecutor) {
      // V2: coreNetwork 기반으로 tx executor 생성
      switch (this.coin?.coreNetwork) {
        case 'SOLANA':
          this._transactionExecutor = new SolanaNftTransactionExecutor({
            nft: this,
          })
          break
        default:
          // 기본: EVM 계열 네트워크
          this._transactionExecutor = new EthereumNftTransactionExecutor({
            nft: this,
          })
      }
    }
    return this._transactionExecutor
  }

  public async getPrepareData(params: { to: string; amount: string }) {
    if (!this.transactionExecutor) {
      throw new Error('transactionExecutor is not exist')
    }
    return this.transactionExecutor.getPrepareData({
      to: params.to,
      amount: params.amount,
    })
  }
  async send(params: {
    to: string
    amount: string
    extraData: ISendExtraData
    encUVD: EncUVD
    otpCode?: { code: string; isRecoveryCode?: boolean }
  }) {
    if (!this.transactionExecutor) {
      throw new Error('transactionExecutor is not exist')
    }
    const signResponse = await this.transactionExecutor.sign({
      to: params.to,
      amount: params.amount,
      extraData: params.extraData,
      encUVD: params.encUVD,
      otpCode: params.otpCode,
    })
    return this.transactionExecutor.broadcast({ signResponse })
  }
}

class NftFactory {
  private static instance: NftFactory
  private constructor() {}

  public static getInstance(): NftFactory {
    if (!NftFactory.instance) {
      NftFactory.instance = new NftFactory()
    }
    return NftFactory.instance
  }

  makeNft(nft: INFT) {
    return new Nft(nft)
  }
}
export { Nft, NftFactory }
